import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  LinearProgress,
  Switch,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { withTranslation, WithTranslation } from 'react-i18next';

import { useAssistant } from '@core/assistant/providers/Provider';
import { SCREENS } from '@core/assistant/interfaces';
import { useGreetings } from '@core/assistant/providers/Greetings';
import { log } from '@core/assistant/providers/Debug';

import DebugOverlay from './DebugOverlay';
import QuizScreen from '../QuizScreen/QuizScreen';
import { useStyles } from './styles';

const DefaultScreen = ({ t: translate }: WithTranslation) => {
  const { generateGreetingsAudio } = useGreetings();
  const classes = useStyles();

  const [showDebugUI, setShowDebugUI] = useState(false);
  const [showScriptQuiz, setShowScriptQuiz] = useState(false);
  const [showNextButton, setShowNextButtton] = useState(false);
  const intervalTimer = useRef<NodeJS.Timer | null>(null);

  const {
    navigate,
    setOpen,
    setNavigationHistory,
    stopPlayback,
    audioPromptHistory,
    startFlow,
    linearScriptPlayerRef,
    isAudioPlaybackActive,
    audioEndTick,
    isScriptCompleted,
    setIsScriptCompleted,
    fetchScripts,
    fetchParticipantProgress,
    resetProgress,
    saveQuizAnswers,
    clearPrerenderedAudio,
    setIsScriptQuizActive,
  } = useAssistant();

  useEffect(() => {
    setIsScriptQuizActive(showScriptQuiz);
  }, [showScriptQuiz]);

  const handleNext = () => {
    setShowNextButtton(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  const handleScriptQuizConfirm = (selectedOptions: number[]) => {
    saveQuizAnswers(selectedOptions);
    setShowScriptQuiz(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  useEffect(() => {
    log('audioPromptHistory', audioPromptHistory);
    log(
      'new item in audioPromptHistory',
      audioPromptHistory[audioPromptHistory.length - 1],
    );
  }, [audioPromptHistory]);

  useEffect(() => {
    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();
    log('linearScriptPlayerState', linearScriptPlayerState);

    const currentNode = linearScriptPlayerState?.currentNode;

    if (currentNode?.questionary?.type === 'multiple_choice') {
      setShowScriptQuiz(true);
      setShowNextButtton(false);
    } else if (!isAudioPlaybackActive && currentNode?.awaitButtonNext) {
      setShowNextButtton(true);
    } else if (!currentNode?.awaitButtonNext) {
      setShowNextButtton(false);
    } else if (linearScriptPlayerState?.isCompleted) {
      setShowNextButtton(false);
    }
  }, [isAudioPlaybackActive, audioEndTick]);

  // dirty fix to disable Next button when script is completed
  useEffect(() => {
    intervalTimer.current = setInterval(() => {
      const linearScriptPlayerState =
        linearScriptPlayerRef.current?.getCurrentState();

      if (!linearScriptPlayerState?.isRunning) {
        setShowNextButtton(false);
      }
    }, 1000);
  }, []);

  const scriptPlayerState = linearScriptPlayerRef.current?.getCurrentState();
  const questionaryInfo = linearScriptPlayerRef.current?.getQuestionaryInfo();
  const maxOptions = linearScriptPlayerRef.current?.getMaxOptionsCount() ?? 0;
  const activeQuestionary = scriptPlayerState?.currentNode?.questionary;
  const activeQuestionText = scriptPlayerState?.currentNode?.text;
  const progressInfo = linearScriptPlayerRef.current?.getProgressInfo();
  const isScriptRunning = scriptPlayerState?.isRunning ?? false;

  return (
    <Box sx={classes.box}>
      {isScriptRunning && progressInfo && (
        <Box sx={classes.progressSection}>
          <LinearProgress
            variant="determinate"
            value={progressInfo.percentage}
            sx={classes.progressBar}
          />
          <Box sx={classes.progressLabels}>
            <Typography sx={classes.progressText}>
              Step {progressInfo.currentStep} of {progressInfo.totalSteps}
            </Typography>
            <Typography sx={classes.progressText}>
              {progressInfo.percentage}%
            </Typography>
          </Box>
        </Box>
      )}

      <Collapse in={isScriptRunning && showNextButton}>
        <Button
          fullWidth
          sx={{ ...classes.button, ...classes.nextButton }}
          onClick={() => handleNext()}
          endIcon={<ArrowForwardIcon />}
        >
          {translate('next')}
        </Button>
      </Collapse>

      {isScriptCompleted && (
        <Box sx={classes.completedBox}>
          <Box sx={classes.completedIconCircle}>
            <CheckIcon sx={{ color: '#4caf50', fontSize: 28 }} />
          </Box>
          <Typography sx={classes.completedTitle}>Script completed</Typography>
          <Typography sx={classes.completedSubtitle}>
            All steps done.
          </Typography>
          <Button
            fullWidth
            sx={{ ...classes.button, ...classes.blueButton }}
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              setIsScriptCompleted(false);
              stopPlayback();
              setOpen(false);
              setNavigationHistory([]);
              navigate(SCREENS.DEFAULT);
            }}
          >
            Continue
          </Button>
        </Box>
      )}

      {!isScriptCompleted && (
        <Button
          sx={{
            ...classes.button,
            ...classes.blueButton,
            mt: isScriptRunning && showNextButton ? 1 : 0,
            mb: 1,
          }}
          onClick={() => navigate(SCREENS.CHAT)}
        >
          {translate('assistant.askMe.title')}
        </Button>
      )}

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={showDebugUI}
              onChange={() => setShowDebugUI(!showDebugUI)}
            />
          }
          sx={{
            color: '#fff',
          }}
          label="Show debug"
        />
      </Box>
      <Button
        sx={classes.hideButton}
        onClick={() => {
          stopPlayback();
          setOpen(false);
          setNavigationHistory([]);
          navigate(SCREENS.DEFAULT);
        }}
      >
        {translate('assistant.hideNoa')}
      </Button>

      {showScriptQuiz && activeQuestionary && activeQuestionText && (
        <QuizScreen
          questionText={activeQuestionText}
          questionary={activeQuestionary}
          questionNumber={questionaryInfo?.current}
          totalQuestions={questionaryInfo?.total}
          maxOptions={maxOptions}
          onConfirm={handleScriptQuizConfirm}
        />
      )}

      {showDebugUI && (
        <DebugOverlay
          startFlow={(flowId) => {
            setShowDebugUI(false);
            startFlow(flowId);
          }}
          stopPlayer={() => {
            linearScriptPlayerRef.current?.stop();
            setShowScriptQuiz(false);
          }}
          generateGreetingsAudio={generateGreetingsAudio}
          fetchScripts={fetchScripts}
          fetchParticipantProgress={fetchParticipantProgress}
          resetProgress={resetProgress}
          clearPrerenderedAudio={clearPrerenderedAudio}
        />
      )}
    </Box>
  );
};

export default withTranslation('common')(DefaultScreen);
